import { gsap } from 'gsap';

/**
 * Unified Animation System - COMPLETELY FIXED VERSION
 * ✅ Smooth mouse startup (no jumps on page load)
 * ✅ Single global mouse tracker 
 * ✅ Perfect property conflict resolution
 * ✅ Full integration with frontend.js
 */
class UnifiedAnimationSystem {
    constructor() {
        this.elements = new Map();
        this.effectModules = new Map();
        this.isRunning = false;
        this.frameCount = 0;
        this.lastPerformanceCheck = 0;
        
        // ✅ FIXED: Enhanced mouse tracking with smooth startup
        this.mouseTracking = {
            position: { x: 0, y: 0 },           // Smooth interpolated position
            realPosition: { x: 0, y: 0 },       // Actual cursor position
            hasRealPosition: false,              // Has user moved mouse yet?
            firstMoveTime: null,                 // When first move happened
            isSmoothing: false,                  // Is smooth transition active?
            smoothingSpeed: 0.05                 // Base smoothing speed
        };
        
        this.sharedState = {
            mousePosition: this.mouseTracking.position, // Reference to smooth position
            scrollY: 0,
            scrollX: 0,
            viewport: { width: 0, height: 0 },
            timestamp: 0,
            deltaTime: 0,
            isVisible: true
        };
        
        this.initializeGlobalTracking();
        this.startGlobalTicker();
        
        // console.log('🚀 Unified Animation System FIXED - with smooth mouse startup');
    }
    
    /**
     * ✅ FIXED: Initialize global tracking with smooth mouse startup
     */
    initializeGlobalTracking() {
        // ✅ CRITICAL: Initialize mouse position to center
        this.mouseTracking.position.x = window.innerWidth / 2;
        this.mouseTracking.position.y = window.innerHeight / 2;
        this.mouseTracking.realPosition.x = window.innerWidth / 2;
        this.mouseTracking.realPosition.y = window.innerHeight / 2;
        
        // ✅ SMOOTH MOUSE STARTUP: The brilliant feature from old system!
        document.addEventListener('mousemove', (e) => {
            this.mouseTracking.realPosition.x = e.clientX;
            this.mouseTracking.realPosition.y = e.clientY;
            
            if (!this.mouseTracking.hasRealPosition) {
                // ✅ First mouse move - start smooth transition
                this.startSmoothMouseTransition();
            } else if (!this.mouseTracking.isSmoothing) {
                // ✅ Normal tracking - direct update
                this.mouseTracking.position.x = e.clientX;
                this.mouseTracking.position.y = e.clientY;
            }
            // ✅ If smoothing is active, smooth() function handles updates
        }, { passive: true });
        
        // Scroll tracking  
        window.addEventListener('scroll', () => {
            this.sharedState.scrollY = window.scrollY || window.pageYOffset;
            this.sharedState.scrollX = window.scrollX || window.pageXOffset;
        }, { passive: true });
        
        // Viewport tracking
        window.addEventListener('resize', () => {
            this.sharedState.viewport.width = window.innerWidth;
            this.sharedState.viewport.height = window.innerHeight;
            this.refreshAllCaches(); // Refresh element rects on resize
        });
        
        // Visibility tracking
        document.addEventListener('visibilitychange', () => {
            this.sharedState.isVisible = !document.hidden;
        });
        
        // Initial values
        this.sharedState.viewport.width = window.innerWidth;
        this.sharedState.viewport.height = window.innerHeight;
        
        // console.log('🖱️ Global tracking initialized with smooth mouse startup');
    }
    
    /**
     * ✅ NEW: Start smooth mouse transition (prevents jumps on page load)
     */
    startSmoothMouseTransition() {
        this.mouseTracking.hasRealPosition = true;
        this.mouseTracking.firstMoveTime = performance.now();
        this.mouseTracking.isSmoothing = true;
        
        // console.log('🎯 Starting smooth mouse transition');
        
        const smooth = () => {
            if (!this.mouseTracking.isSmoothing) return;
            
            const elapsed = performance.now() - this.mouseTracking.firstMoveTime;
            
            // ✅ GRADUAL SPEED INCREASE: 0.05 -> 0.9 over 1.5 seconds
            const progress = Math.min(elapsed / 1500, 1);
            const speed = this.mouseTracking.smoothingSpeed + (0.85 * progress);
            
            // ✅ SMOOTH INTERPOLATION
            const dx = this.mouseTracking.realPosition.x - this.mouseTracking.position.x;
            const dy = this.mouseTracking.realPosition.y - this.mouseTracking.position.y;
            
            this.mouseTracking.position.x += dx * speed;
            this.mouseTracking.position.y += dy * speed;
            
            // ✅ CONTINUE SMOOTHING until close enough or time elapsed
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > 2 && elapsed < 1500) {
                requestAnimationFrame(smooth);
            } else {
                this.mouseTracking.isSmoothing = false;
                // console.log('✅ Smooth mouse transition complete');
            }
        };
        
        smooth();
    }
    
    startGlobalTicker() {
        if (this.isRunning) return;
        
        let lastTime = performance.now();
        
        const globalUpdate = () => {
            if (this.elements.size === 0) return;
            
            const currentTime = performance.now();
            this.sharedState.deltaTime = currentTime - lastTime;
            this.sharedState.timestamp = currentTime;
            lastTime = currentTime;
            
            if (!this.sharedState.isVisible) return;
            
            this.updateAllElements();
            
            this.frameCount++;
            if (currentTime - this.lastPerformanceCheck > 5000) {
                this.checkPerformance();
                this.lastPerformanceCheck = currentTime;
            }
        };
        
        gsap.ticker.add(globalUpdate);
        this.isRunning = true;
        
        // console.log('🎬 Global animation ticker started');
    }
    
    registerModule(name, module) {
        this.effectModules.set(name, module);
        // console.log(`🧩 Registered effect module: ${name}`);
    }
    
    /**
     * ✅ ENHANCED: Register element with perfect conflict resolution
     */
    registerElement(element, config) {
        if (!element || !element.nodeType) {
            console.error('❌ Invalid element provided to registerElement');
            return null;
        }
        
        const elementId = element.id || `unified-element-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        const elementData = {
            id: elementId,
            element: element,
            config: config,
            activeEffects: new Map(),
            
            // ✅ ENHANCED: Property ownership with priority levels
            propertyOwnership: new Map(), // property -> { owner: 'effectName', priority: number }
            
            cache: {
                rect: null,
                lastRectUpdate: 0,
                needsRectUpdate: true
            },
            performance: {
                updateCount: 0,
                avgUpdateTime: 0
            },
            lastUpdate: 0
        };
        
        // ✅ ENHANCED: Setup effects with perfect priority system
        this.setupElementEffectsEnhanced(element, elementData, config);
        
        // Register element
        this.elements.set(element, elementData);
        
        // console.log(`✅ Registered element: ${elementId} with ${elementData.activeEffects.size} effects`);
        return elementId;
    }
    
    /**
     * ✅ ENHANCED: Setup effects with perfect priority and conflict resolution
     */
    setupElementEffectsEnhanced(element, elementData, config) {
        // ✅ PRIORITY LEVELS (higher number = higher priority)
        const PRIORITY = {
            PARALLAX: 100,        // Highest - controls position
            BASIC_ANIMATIONS: 80, // High - controls transforms, colors, effects
            MOUSE_DISTANCE: 60,   // Medium - delegates to basic animations
            TEXT_ANIMATIONS: 40   // Low - independent text effects
        };
        
        const effects = [];
        
        // ✅ STEP 1: Collect all effects with priorities
        if (config.useParallax || config.useMouseParallax) {
            const parallaxModule = this.effectModules.get('parallax');
            if (parallaxModule) {
                effects.push({
                    name: 'parallax',
                    module: parallaxModule,
                    config: config,
                    priority: PRIORITY.PARALLAX,
                    properties: ['x', 'y']
                });
            }
        }
        
        if (config.basicAnimations?.enabled) {
            const basicModule = this.effectModules.get('basicAnimations');
            if (basicModule) {
                effects.push({
                    name: 'basicAnimations',
                    module: basicModule,
                    config: config,
                    priority: PRIORITY.BASIC_ANIMATIONS,
                    properties: ['opacity', 'scale', 'rotation', 'skewX', 'skewY', 'blur', 'backgroundColor', 'textColor', 'borderRadius', 'clipPath']
                });
            }
        }
        
        if (config.eventsConfig?.triggerType === 'mousePosition') {
            const mouseModule = this.effectModules.get('mouseDistance');
            if (mouseModule) {
                effects.push({
                    name: 'mouseDistance',
                    module: mouseModule,
                    config: config,
                    priority: PRIORITY.MOUSE_DISTANCE,
                    properties: [] // Will be linked to basic animations
                });
            }
        }
        
        if (config.advancedAnimations?.textAnimations?.enabled) {
            const textModule = this.effectModules.get('textAnimations');
            if (textModule) {
                effects.push({
                    name: 'textAnimations',
                    module: textModule,
                    config: config.advancedAnimations.textAnimations,
                    priority: PRIORITY.TEXT_ANIMATIONS,
                    properties: [] // Text elements only
                });
            }
        }
        
        // ✅ STEP 2: Sort by priority (highest first)
        effects.sort((a, b) => b.priority - a.priority);
        
        // ✅ STEP 3: Register effects and assign property ownership
        effects.forEach(effectInfo => {
            const effectState = effectInfo.module.createState(element, effectInfo.config);
            
            if (effectState && effectState.enabled !== false) {
                // Register effect
                elementData.activeEffects.set(effectInfo.name, {
                    module: effectInfo.module,
                    state: effectState,
                    enabled: true,
                    priority: effectInfo.priority
                });
                
                // ✅ ASSIGN PROPERTY OWNERSHIP (first come, first served by priority)
                effectInfo.properties.forEach(property => {
                    if (!elementData.propertyOwnership.has(property)) {
                        elementData.propertyOwnership.set(property, {
                            owner: effectInfo.name,
                            priority: effectInfo.priority
                        });
                    }
                });
                
                // console.log(`✅ ${effectInfo.name} registered (priority: ${effectInfo.priority})`);
            }
        });
        
        // ✅ STEP 4: Setup special effect linking
        const basicEffect = elementData.activeEffects.get('basicAnimations');
        const mouseEffect = elementData.activeEffects.get('mouseDistance');
        
        if (basicEffect && mouseEffect && mouseEffect.module.linkBasicAnimations) {
            mouseEffect.module.linkBasicAnimations(mouseEffect.state, basicEffect.state);
            // console.log(`🔗 Linked mouse distance to basic animations`);
        }
        
        // ✅ STEP 5: Add debug CSS classes
        element.classList.add('unified-animation-element');
        elementData.activeEffects.forEach((effectData, effectName) => {
            element.classList.add(`unified-${effectName}`);
        });
        
        // ✅ LOG FINAL SETUP
        const ownerships = {};
        elementData.propertyOwnership.forEach((ownerInfo, prop) => {
            if (!ownerships[ownerInfo.owner]) ownerships[ownerInfo.owner] = [];
            ownerships[ownerInfo.owner].push(prop);
        });
        
        // console.log(`🎯 Element ${elementData.id} setup complete:`, {
        //     effects: Array.from(elementData.activeEffects.keys()),
        //     propertyOwnership: ownerships
        // });
    }
    
    updateAllElements() {
        this.elements.forEach((elementData, element) => {
            this.updateElement(element, elementData);
        });
    }
    
    updateElement(element, elementData) {
        // Check if element is still in DOM
        if (!element.isConnected) {
            this.unregisterElement(element);
            return;
        }
        
        // const currentState = {
        //     progress: elementData.activeEffects.get('basicAnimations')?.state?.currentProgress || 0,
        //     isAnimating: elementData.activeEffects.get('basicAnimations')?.state?.isAnimating || false,
        //     lastUpdate: elementData.lastUpdate
        // };

        // console.log( currentState )

        const startTime = performance.now();
        
        // Update cached rect if needed
        this.updateElementCache(element, elementData);
        
        // ✅ ENHANCED: Update effects in priority order (highest to lowest)
        const sortedEffects = Array.from(elementData.activeEffects.entries())
            .sort(([,a], [,b]) => (b.priority || 0) - (a.priority || 0));
        
        sortedEffects.forEach(([effectName, effectData]) => {
            if (effectData.enabled && effectData.module) {
                try {
                    effectData.module.update(
                        element, 
                        effectData.state, 
                        this.sharedState, 
                        this.sharedState.deltaTime,
                        elementData.cache
                    );
                } catch (error) {
                    console.error(`❌ Error updating effect ${effectName}:`, error);
                    effectData.enabled = false;
                }
            }
        });
        
        // Performance tracking
        const updateTime = performance.now() - startTime;
        elementData.performance.updateCount++;
        elementData.performance.avgUpdateTime = 
            (elementData.performance.avgUpdateTime + updateTime) / 2;
        
        elementData.lastUpdate = this.sharedState.timestamp;
    }
    
    updateElementCache(element, elementData) {
        const now = this.sharedState.timestamp;
        const cache = elementData.cache;
        
        // Update rect every 100ms or when explicitly needed
        if (cache.needsRectUpdate || (now - cache.lastRectUpdate) > 100) {
            cache.rect = element.getBoundingClientRect();
            cache.lastRectUpdate = now;
            cache.needsRectUpdate = false;
        }
    }
    
    /**
     * ✅ ENHANCED: Get current mouse position (always smooth)
     */
    getMousePosition() {
        return { ...this.mouseTracking.position };
    }
    
    /**
     * ✅ NEW: Check if smooth mouse transition is active
     */
    isMouseSmoothing() {
        return this.mouseTracking.isSmoothing;
    }
    
    /**
     * ✅ NEW: Force smooth mouse transition (useful for testing)
     */
    forceSmoothMouseTransition() {
        this.mouseTracking.hasRealPosition = false;
        this.mouseTracking.isSmoothing = false;
    }
    
    unregisterElement(element) {
        const elementData = this.elements.get(element);
        if (!elementData) return;
        
        // Cleanup all effect states
        elementData.activeEffects.forEach((effectData, effectName) => {
            if (effectData.module.cleanup) {
                try {
                    effectData.module.cleanup(effectData.state);
                } catch (error) {
                    console.error(`❌ Error cleaning up effect ${effectName}:`, error);
                }
            }
        });
        
        // Remove CSS classes
        element.classList.remove('unified-animation-element');
        elementData.activeEffects.forEach((effectData, effectName) => {
            element.classList.remove(`unified-${effectName}`);
        });
        
        // Remove from registry
        this.elements.delete(element);
        
        // console.log(`🗑️ Unregistered element: ${elementData.id}`);
    }
    
    checkPerformance() {
        const fps = this.frameCount / 5;
        this.frameCount = 0;
        
        if (fps < 50) {
            console.warn(`⚠️ Low FPS detected: ${fps.toFixed(1)}fps. Elements: ${this.elements.size}`);
            this.optimizePerformance();
        } else {
            // console.log(`✅ Performance OK: ${fps.toFixed(1)}fps, ${this.elements.size} elements`);
        }
    }
    
    optimizePerformance() {
        this.elements.forEach((elementData, element) => {
            if (elementData.performance.avgUpdateTime > 5) {
                console.warn(`🐌 Slow element detected: ${elementData.id}, optimizing...`);
                
                // Disable heavy effects on slow elements
                const textEffect = elementData.activeEffects.get('textAnimations');
                if (textEffect) {
                    textEffect.enabled = false;
                    // console.log(`⚡ Disabled text animations on slow element ${elementData.id}`);
                }
            }
        });
    }
    
    refreshAllCaches() {
        this.elements.forEach((elementData) => {
            elementData.cache.needsRectUpdate = true;
        });
    }
    
    /**
     * ✅ ENHANCED: Get comprehensive system statistics
     */
    getStats() {
        const conflictStats = {};
        this.elements.forEach((elementData) => {
            elementData.propertyOwnership.forEach((ownerInfo, prop) => {
                if (!conflictStats[ownerInfo.owner]) conflictStats[ownerInfo.owner] = [];
                conflictStats[ownerInfo.owner].push(prop);
            });
        });
        
        return {
            elementsCount: this.elements.size,
            modulesCount: this.effectModules.size,
            isRunning: this.isRunning,
            mouseTracking: {
                hasRealPosition: this.mouseTracking.hasRealPosition,
                isSmoothing: this.mouseTracking.isSmoothing,
                currentPosition: this.getMousePosition()
            },
            propertyOwnership: conflictStats,
            performance: {
                fps: this.frameCount > 0 ? this.frameCount / 5 : 0,
                avgElementUpdateTime: this.getAverageElementUpdateTime()
            }
        };
    }
    
    getAverageElementUpdateTime() {
        let totalTime = 0;
        let count = 0;
        
        this.elements.forEach((elementData) => {
            if (elementData.performance.avgUpdateTime > 0) {
                totalTime += elementData.performance.avgUpdateTime;
                count++;
            }
        });
        
        return count > 0 ? totalTime / count : 0;
    }
    
    destroy() {
        // Unregister all elements
        this.elements.forEach((elementData, element) => {
            this.unregisterElement(element);
        });
        
        // Stop ticker
        gsap.ticker.remove(this.updateAllElements);
        this.isRunning = false;
        
        // console.log('🛑 Unified Animation System destroyed');
    }
}

// Create global instance
let animationSystem = null;

/**
 * Get or create the global animation system instance
 */
export function getAnimationSystem() {
    if (!animationSystem) {
        animationSystem = new UnifiedAnimationSystem();
    }
    return animationSystem;
}

/**
 * Initialize the animation system
 */
export function initializeAnimationSystem() {
    const system = getAnimationSystem();
    // console.log('🎯 Animation system ready for module registration');
    return system;
}

export default UnifiedAnimationSystem;